import { Buffer } from "buffer"

// Hermes lacks some web globals that dependencies reference at module-eval time
// (i.e. while being require()'d, before React is ready). This module is imported
// first in index.js so the globals exist before the app's module graph loads —
// an inline polyfill in index.js runs too late, since Metro hoists imports above
// the module body and the offending dependency is require()'d during that phase.

if (typeof globalThis.TextDecoder === "undefined") {
    globalThis.TextDecoder = class {
        decode(input) {
            return Buffer.from(input).toString("utf8")
        }
    }
}

if (typeof globalThis.DOMException === "undefined") {
    globalThis.DOMException = class DOMException extends Error {
        constructor(message = "", name = "Error") {
            super(message)
            this.name = name
        }
    }
}

// livekit-client's signal abort handler (src/api/SignalClient.ts, abortHandler)
// does `eventOrError instanceof Event`. On Hermes that is a ReferenceError thrown
// at the exact moment the SDK should start a reconnection. React Native ships an
// EventPolyfill but never loads it on this version (facebook/react-native#38004).
// A minimal Event is enough for that check. event-target-shim (react-native-webrtc,
// AbortSignal) also reads this global and chains its own Event.prototype onto ours.
if (typeof globalThis.Event === "undefined") {
    globalThis.Event = class Event {
        constructor(type, init) {
            const options = init ?? {}
            this.type = String(type)
            this.bubbles = !!options.bubbles
            this.cancelable = !!options.cancelable
            this.composed = !!options.composed
            this.defaultPrevented = false
            this.currentTarget = null
            this.timeStamp = Date.now()
        }

        preventDefault() {
            if (this.cancelable) this.defaultPrevented = true
        }

        stopPropagation() {}

        stopImmediatePropagation() {}
    }
}

// React Native's own core (Libraries/Core/setUpXHR.js) unconditionally polyfills
// global AbortController/AbortSignal from the `abort-controller` npm package
// (v3), whose `abort()` takes no argument and whose AbortSignal has no `.reason`
// property at all. livekit-client's getAbortReasonAsString (src/api/utils.ts)
// reads `signal.reason` when a connection attempt is aborted (room.disconnect()
// aborting an in-flight room.connect()), gets `undefined`, and falls into
// `'toString' in reason` — a TypeError, uncaught, that crashes the app. Give the
// polyfilled signal a real `.reason`, guarded so a future RN/Hermes version that
// already supports it is left untouched.
if (
    typeof globalThis.AbortController !== "undefined" &&
    typeof globalThis.AbortSignal !== "undefined" &&
    !Object.getOwnPropertyDescriptor(globalThis.AbortSignal.prototype, "reason")
) {
    const originalAbort = globalThis.AbortController.prototype.abort
    globalThis.AbortController.prototype.abort = function abort(reason) {
        Object.defineProperty(this.signal, "reason", {
            configurable: true,
            enumerable: true,
            value: reason,
        })
        return originalAbort.call(this)
    }
}
